import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert, Autocomplete, Box, Button, Checkbox, CircularProgress, FormControlLabel,
  Paper, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import SendIcon from '@mui/icons-material/Send'
import { supabase } from '../../supabaseClient'
import { useAuth } from '../../auth/AuthContext'

// Outbound message: pick a recipient (class / student / parent / staff),
// write it or have AI draft it, then send explicitly. Drafting via
// draft-outbound-message; sending via send-class-message (class) or
// send-direct-email (everyone else).
//
// Subject and Message are always available. They used to appear only once AI
// had returned a draft, which made "type my own message and send it" a thing
// the page could not do — the intent box and the Draft button were the only
// road out. That also meant anyone who typed their real message into the intent
// box had it read as a brief and answered with words they had not written,
// which is what "Polish what I wrote" is for: same AI, their words kept.

type RecipientType = 'class' | 'student' | 'parent' | 'staff'

interface ClassOption   { class_id: string; name: string }
interface PersonOption  { id: string; label: string }   // student_id or user_id

const TYPE_LABEL: Record<RecipientType, string> = {
  class: 'Class', student: 'Student', parent: 'Parent', staff: 'Staff',
}

export default function ComposePage() {
  const { session, role } = useAuth()

  const [recipientType, setRecipientType] = useState<RecipientType>('class')
  const [classes,  setClasses]  = useState<ClassOption[]>([])
  const [students, setStudents] = useState<PersonOption[]>([])
  const [staff,    setStaff]    = useState<PersonOption[]>([])
  const [pickedClass,   setPickedClass]   = useState<ClassOption | null>(null)
  const [pickedStudent, setPickedStudent] = useState<PersonOption | null>(null)
  const [pickedStaff,   setPickedStaff]   = useState<PersonOption | null>(null)
  const [ccParents, setCcParents] = useState(false)

  // Resolved recipient summary (class recipient count / parent names)
  const [resolved, setResolved] = useState<string | null>(null)

  const [intent,      setIntent]      = useState('')
  const [subject,     setSubject]     = useState('')
  const [body,        setBody]        = useState('')
  const [instruction, setInstruction] = useState('')
  const [drafting,    setDrafting]    = useState(false)
  const [sending,     setSending]     = useState(false)
  const [error,       setError]       = useState<string | null>(null)
  const [result,      setResult]      = useState<string | null>(null)

  const hasDraft = !!(subject.trim() || body.trim())

  // ── Load recipient options ──────────────────────────────────────────────────

  useEffect(() => {
    if (!session) return
    ;(async () => {
      // Classes: admin/OM see all; mentors/instructors see the classes they mentor.
      if (role === 'admin' || role === 'office_manager') {
        const { data } = await supabase.from('class')
          .select('class_id, name').in('status', ['upcoming', 'active']).order('name')
        setClasses(data ?? [])
      } else {
        const { data: cm } = await supabase.from('class_mentor')
          .select('class_id').eq('user_id', session.user.id)
        const ids = (cm ?? []).map(r => r.class_id)
        if (ids.length === 0) { setClasses([]) } else {
          const { data } = await supabase.from('class')
            .select('class_id, name').in('class_id', ids).order('name')
          setClasses(data ?? [])
        }
      }

      // Students: active research students (server enforces per-student
      // permission on send, so the list can be broad).
      const { data: profiles } = await supabase.from('student_profile')
        .select('student_id, user_id').in('lifecycle_state', ['enrolled_active', 'enrolled_completed'])
      const userIds = (profiles ?? []).map(p => p.user_id)
      if (userIds.length > 0) {
        const { data: users } = await supabase.from('users')
          .select('user_id, first_name, last_name').in('user_id', userIds)
        const nameById = Object.fromEntries((users ?? []).map(u =>
          [u.user_id, `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim()]))
        setStudents((profiles ?? [])
          .map(p => ({ id: p.student_id, label: nameById[p.user_id] || '—' }))
          .sort((a, b) => a.label.localeCompare(b.label)))
      }

      // Staff: every active staff member except me.
      const { data: staffRows } = await supabase.from('users')
        .select('user_id, first_name, last_name, role')
        .in('role', ['admin', 'office_manager', 'instructor', 'mentor'])
        .eq('status', 'active')
      setStaff((staffRows ?? [])
        .filter(u => u.user_id !== session.user.id)
        .map(u => ({ id: u.user_id, label: `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || u.user_id }))
        .sort((a, b) => a.label.localeCompare(b.label)))
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user.id, role])

  // ── Resolve recipients for confirmation line ────────────────────────────────

  useEffect(() => {
    setResolved(null)
    ;(async () => {
      if (recipientType === 'class' && pickedClass) {
        const { data } = await supabase.functions.invoke('send-class-message', {
          body: { class_id: pickedClass.class_id, dry_run: true },
        })
        if (typeof data?.recipients === 'number') {
          setResolved(`${data.recipients} enrolled student${data.recipients === 1 ? '' : 's'}${ccParents ? ', parents CC\'d' : ''}`)
        }
      } else if (recipientType === 'parent' && pickedStudent) {
        const { data } = await supabase.functions.invoke('send-direct-email', {
          body: { kind: 'parent', student_id: pickedStudent.id, dry_run: true },
        })
        if (data?.recipients?.length) setResolved(`Parent(s) of ${pickedStudent.label}: ${data.recipients.join(', ')}`)
        else if (data?.error) setResolved(data.error)
      } else if (recipientType === 'student' && pickedStudent) {
        setResolved(pickedStudent.label)
      } else if (recipientType === 'staff' && pickedStaff) {
        setResolved(pickedStaff.label)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipientType, pickedClass, pickedStudent, pickedStaff, ccParents])

  const recipientChosen = useMemo(() => {
    if (recipientType === 'class') return !!pickedClass
    if (recipientType === 'staff') return !!pickedStaff
    return !!pickedStudent
  }, [recipientType, pickedClass, pickedStudent, pickedStaff])

  // ── AI draft / revise ───────────────────────────────────────────────────────

  type DraftMode = 'draft' | 'revise' | 'polish'

  async function draft(mode: DraftMode) {
    setError(null); setDrafting(true)
    const { data, error: invokeErr } = await supabase.functions.invoke('draft-outbound-message', {
      body: {
        recipient_type: recipientType,
        intent:         mode === 'draft' ? intent.trim() : undefined,
        class_id:       recipientType === 'class'   ? pickedClass?.class_id : undefined,
        student_id:     recipientType !== 'class' && recipientType !== 'staff' ? pickedStudent?.id : undefined,
        staff_user_id:  recipientType === 'staff'   ? pickedStaff?.id : undefined,
        current:        mode === 'draft' ? undefined : { subject, body },
        instruction:    mode === 'revise' ? instruction.trim() : undefined,
        polish:         mode === 'polish' ? true : undefined,
      },
    })
    setDrafting(false)
    if (invokeErr || data?.error) { setError(data?.error ?? 'Could not draft the message. Please try again.'); return }
    setSubject(data.subject); setBody(data.body)
    if (mode === 'revise') setInstruction('')
  }

  // ── Send ────────────────────────────────────────────────────────────────────

  async function send() {
    setError(null); setSending(true)
    let data: { error?: string; sent?: number; sent_to?: string[] } | null = null
    let invokeErr: unknown = null
    if (recipientType === 'class') {
      ;({ data, error: invokeErr } = await supabase.functions.invoke('send-class-message', {
        body: { class_id: pickedClass!.class_id, subject: subject.trim(), body: body.trim(), cc_parents: ccParents },
      }))
    } else {
      ;({ data, error: invokeErr } = await supabase.functions.invoke('send-direct-email', {
        body: {
          kind:       recipientType,
          user_id:    recipientType === 'staff' ? pickedStaff!.id : undefined,
          student_id: recipientType !== 'staff' ? pickedStudent!.id : undefined,
          subject:    subject.trim(),
          text:       body.trim(),
        },
      }))
    }
    setSending(false)
    if (invokeErr || data?.error) { setError(data?.error ?? 'Could not send. Please try again.'); return }
    const who = recipientType === 'class'
      ? `${data?.sent ?? 0} student${data?.sent === 1 ? '' : 's'}${ccParents ? ' (parents CC\'d)' : ''}`
      : (data?.sent_to ?? []).join(', ')
    setResult(`Message sent to ${who}.`)
    setSubject(''); setBody(''); setIntent(''); setInstruction('')
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: 760 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>Send a Message</Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Write it yourself — AI can polish your words, or draft from scratch if you say what the
        message should accomplish. Either way nothing sends without you.
      </Typography>

      {result && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setResult(null)}>{result}</Alert>}
      {error &&  <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
        {/* Recipient */}
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>To</Typography>
        <ToggleButtonGroup
          exclusive size="small" value={recipientType}
          onChange={(_, v: RecipientType | null) => { if (v) { setRecipientType(v); setResolved(null) } }}
          sx={{ mb: 2 }}
        >
          {(Object.keys(TYPE_LABEL) as RecipientType[]).map(t => (
            <ToggleButton key={t} value={t} sx={{ px: 2 }}>{TYPE_LABEL[t]}</ToggleButton>
          ))}
        </ToggleButtonGroup>

        {recipientType === 'class' && (
          <>
            <Autocomplete
              options={classes} getOptionLabel={o => o.name} value={pickedClass}
              onChange={(_, v) => setPickedClass(v)}
              renderInput={p => <TextField {...p} label="Class" size="small" />}
              sx={{ mb: 1 }}
            />
            <FormControlLabel
              control={<Checkbox checked={ccParents} onChange={e => setCcParents(e.target.checked)} />}
              label={<Typography variant="body2">CC parents</Typography>}
            />
          </>
        )}
        {(recipientType === 'student' || recipientType === 'parent') && (
          <Autocomplete
            options={students} getOptionLabel={o => o.label} value={pickedStudent}
            onChange={(_, v) => setPickedStudent(v)}
            renderInput={p => <TextField {...p} label={recipientType === 'parent' ? 'Parent of student' : 'Student'} size="small" />}
          />
        )}
        {recipientType === 'staff' && (
          <Autocomplete
            options={staff} getOptionLabel={o => o.label} value={pickedStaff}
            onChange={(_, v) => setPickedStaff(v)}
            renderInput={p => <TextField {...p} label="Staff member" size="small" />}
          />
        )}
        {resolved && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Will email: <strong>{resolved}</strong>
          </Typography>
        )}

        {/* Intent → draft */}
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 3, mb: 0.25 }}>
          Want AI to draft it? <Typography component="span" variant="caption" color="text.secondary" sx={{ fontWeight: 400 }}>(optional — or just write it below)</Typography>
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Describe the purpose, not the wording — AI writes the message from it.
        </Typography>
        <TextField
          placeholder='e.g. "Remind everyone to bring their lab notebooks Saturday" or "Ask him to come to office hours this week"'
          value={intent} onChange={e => setIntent(e.target.value)}
          fullWidth multiline minRows={2} size="small" disabled={drafting || sending}
        />
        <Button
          variant="outlined" startIcon={drafting && !hasDraft ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
          onClick={() => draft('draft')} disabled={!recipientChosen || !intent.trim() || drafting || sending}
          sx={{ mt: 1.5 }}
        >
          {/* "Redraft" implied the box held an AI draft. It may hold your own
              writing, which this button discards — say so. */}
          {hasDraft ? 'Replace with AI draft' : 'Draft with AI'}
        </Button>

        {/* The message itself — always available, with or without AI. */}
        {(
          <>
            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2, mt: 3, mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Message — write or edit freely</Typography>
              {/* Same AI, the other direction: this one keeps your wording. */}
              <Button
                size="small" onClick={() => draft('polish')}
                disabled={!body.trim() || drafting || sending}
                startIcon={drafting ? <CircularProgress size={14} /> : <AutoAwesomeIcon fontSize="small" />}
                sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                Polish what I wrote
              </Button>
            </Box>
            <TextField
              label="Subject" value={subject} onChange={e => setSubject(e.target.value)}
              fullWidth size="small" sx={{ mb: 2 }} disabled={sending}
            />
            <TextField
              label="Message" value={body} onChange={e => setBody(e.target.value)}
              fullWidth multiline minRows={8} maxRows={18} disabled={sending}
              helperText={recipientType === 'class' ? '[Student] is replaced with each student\'s first name.' : 'This is exactly what the recipient receives.'}
              slotProps={{ input: { sx: { fontSize: 14, lineHeight: 1.6 } } }}
            />
            {hasDraft && (
            <Box sx={{ display: 'flex', gap: 1, mt: 1.5, alignItems: 'center' }}>
              <TextField
                placeholder='Revise: e.g. "shorter", "mention the science fair date"'
                value={instruction} onChange={e => setInstruction(e.target.value)}
                size="small" fullWidth disabled={drafting || sending}
                onKeyDown={e => { if (e.key === 'Enter' && instruction.trim()) { e.preventDefault(); draft('revise') } }}
              />
              <Button
                variant="outlined" onClick={() => draft('revise')}
                disabled={!instruction.trim() || drafting || sending}
                startIcon={drafting ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Revise
              </Button>
            </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                variant="contained" startIcon={sending ? <CircularProgress size={16} /> : <SendIcon />}
                onClick={send}
                disabled={sending || drafting || !recipientChosen || !subject.trim() || !body.trim()}
              >
                {resolved ? `Send to ${resolved.split(':')[0]}` : 'Send'}
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  )
}
